// API Configuration
// Resolve the backend base URL so the app works both locally and when reached
// from another machine on the LAN (e.g. http://192.168.x.x:3000).
const resolveApiBaseUrl = (): string => {
  // Explicit override always wins (e.g. set in production builds).
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    // Dev: CRA dev server runs on :3000 while the backend runs on :8000.
    // Derive the backend host from whatever host the browser used.
    if (port === '3000') {
      return `${protocol}//${hostname}:8000/api`;
    }
  }
  // Production / behind a reverse proxy: same-origin relative path.
  return '/api';
};

export const API_BASE_URL = resolveApiBaseUrl();

// Google OAuth client ID (from Google Cloud Console)
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Facebook (Meta) app ID (from the Meta app dashboard). Public value used to
// initialize the Facebook JS SDK. Empty disables the Facebook login button.
export const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '';

// Master switch for Facebook-app-dependent UI (Login with Facebook + admin
// "Post to Facebook"). Temporarily OFF while the Meta app is still in App
// Review — flip on by setting REACT_APP_FACEBOOK_ENABLED=true. Note: the public
// "Share" (sharer dialog) does not depend on this and stays available.
export const FACEBOOK_FEATURES_ENABLED = process.env.REACT_APP_FACEBOOK_ENABLED === 'true';

// Routes that trigger scroll-to-top behavior
export const SCROLL_TO_TOP_PATHS = ['/', '/privacy', '/terms', '/cookies', '/data-deletion', '/products', '/cart', '/favourites', '/login', '/register'];

// Function to check if a path should trigger scroll to top
export const shouldScrollToTop = (pathname: string): boolean => {
  // Check exact matches
  if (SCROLL_TO_TOP_PATHS.includes(pathname)) {
    return true;
  }
  
  // Check product detail pages (e.g., /products/12)
  if (pathname.match(/^\/products\/\d+$/)) {
    return true;
  }
  
  return false;
};

// Local Storage Keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data',
  GUEST_CART: 'guest_cart',
  GUEST_WISHLIST: 'guest_wishlist',
  ADMIN_MODE: 'admin_mode',
} as const;

// Toast Messages
export const TOAST_MESSAGES = {
  LOGIN_SUCCESS: 'Login successful!',
  LOGIN_FAILED: 'Login failed',
  REGISTER_SUCCESS: 'Registration successful!',
  REGISTER_FAILED: 'Registration failed',
  LOGOUT_SUCCESS: 'Logged out successfully',
  TOKEN_REFRESH_SUCCESS: 'Token refreshed successfully!',
  TOKEN_REFRESH_FAILED: 'Token refresh failed',
  CART_LOAD_FAILED: 'Failed to load cart',
  CART_ADD_SUCCESS: (productName: string) => `${productName} added to cart`,
  CART_ADD_FAILED: 'Failed to add item to cart',
  CART_UPDATE_SUCCESS: 'Cart updated',
  CART_UPDATE_FAILED: 'Failed to update cart',
  CART_REMOVE_SUCCESS: 'Item removed from cart',
  CART_REMOVE_FAILED: 'Failed to remove item',
  LOGIN_REQUIRED: 'Please login to add items to cart',
} as const; 